/**
 * src/pages/scrapers/RemediateTab.tsx
 *
 * Scraper Remediator — 1-click multi-executor diagnostic, in-place executor hot-swap,
 * and automated GitHub PR creation with CI-gated auto-merge.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import {
  CheckCircle2,
  ExternalLink,
  Flame,
  GitPullRequest,
  Loader2,
  Play,
  RotateCcw,
  ShieldAlert,
  Zap,
} from 'lucide-react';
import {
  listRemediationCandidates,
  hotSwapExecutor,
  openRemediationPR,
  getAutoMergeStatus,
  type RemediationCandidate,
} from '@/api/remediate';
import { runCompare, type EngineKind, type ProbeResult } from '@/api/probe-engines';
import { cn } from '@/lib/cn';
import { relativeTime, pct } from '@/lib/format';
import type { Executor } from '@/types';

const CANDIDATE_ENGINES: EngineKind[] = ['cf_http', 'docker_http', 'cf_browser', 'docker_browser'];

const EXECUTOR_MAP: Record<EngineKind, Executor> = {
  direct_http: 'docker',
  cf_http: 'cf_workers',
  docker_http: 'docker',
  cf_browser: 'cf_workers',
  docker_browser: 'docker',
};

export default function RemediateTab() {
  const loc = useLocation();
  const searchParams = new URLSearchParams(loc.search);
  const initialScraperId = searchParams.get('scraperId') ?? '';

  const [selectedId, setSelectedId] = useState<string>(initialScraperId);
  const [blockedOnly, setBlockedOnly] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('iphone 15');
  const [targetExecutor, setTargetExecutor] = useState<Executor>('docker');
  const [autoMergeOnCi, setAutoMergeOnCi] = useState<boolean>(true);

  const [probeResults, setProbeResults] = useState<ProbeResult[] | null>(null);
  const [activePr, setActivePr] = useState<{ number: number; url: string; branch: string } | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  const qc = useQueryClient();

  const candidatesQ = useQuery({
    queryKey: ['remediate-candidates', blockedOnly],
    queryFn: () => listRemediationCandidates({ blockedOnly }),
    refetchInterval: 15_000,
  });

  const candidates = candidatesQ.data ?? [];

  const sortedCandidates = useMemo(() => {
    return [...candidates].sort((a, b) => {
      const aBlocked = a.lastRunStatus === 'blocked' || a.botRate24h > 0.4 ? 1 : 0;
      const bBlocked = b.lastRunStatus === 'blocked' || b.botRate24h > 0.4 ? 1 : 0;
      if (aBlocked !== bBlocked) return bBlocked - aBlocked;
      return a.id.localeCompare(b.id);
    });
  }, [candidates]);

  const currentScraper: RemediationCandidate | undefined = useMemo(() => {
    if (selectedId) {
      const found = sortedCandidates.find((c) => c.id === selectedId);
      if (found) return found;
    }
    const firstBlocked = sortedCandidates.find((c) => c.lastRunStatus === 'blocked' || c.botRate24h > 0.4);
    return firstBlocked ?? sortedCandidates[0];
  }, [sortedCandidates, selectedId]);

  useEffect(() => {
    if (!selectedId && sortedCandidates.length > 0) {
      const firstBlocked = sortedCandidates.find((c) => c.lastRunStatus === 'blocked' || c.botRate24h > 0.4);
      setSelectedId((firstBlocked ?? sortedCandidates[0]).id);
    }
  }, [sortedCandidates, selectedId]);

  useEffect(() => {
    if (currentScraper?.searchTerms && currentScraper.searchTerms.length > 0) {
      setSearchTerm(currentScraper.searchTerms[0]);
    }
  }, [currentScraper]);

  // Compute test URL based on scraper base_url template or default fallback
  const testUrl = useMemo(() => {
    if (!currentScraper) return '';
    const base = currentScraper.baseUrl || `https://www.google.com/search?q={term}`;
    return base.replace('{term}', encodeURIComponent(searchTerm)).replace('%s', encodeURIComponent(searchTerm));
  }, [currentScraper, searchTerm]);

  // Multi-engine compare probe
  const probeM = useMutation({
    mutationFn: async () => {
      if (!testUrl) throw new Error('No target URL available for probe');
      const resp = await runCompare({
        url: testUrl,
        method: 'GET',
        engines: CANDIDATE_ENGINES,
        browser: { screenshot: false, humanevasionTier: 'cdp' },
      });
      return resp.results;
    },
    onSuccess: (results) => {
      setProbeResults(results);
      // Auto-recommend best engine
      const working = results.find((r) => r.blockVerdict === 'ok' && r.statusCode === 200);
      if (working) {
        setTargetExecutor(EXECUTOR_MAP[working.engine] ?? 'docker');
      }
    },
  });

  // Hot-swap mutation
  const hotSwapM = useMutation({
    mutationFn: () => {
      if (!currentScraper) throw new Error('No scraper selected');
      return hotSwapExecutor(currentScraper.id, targetExecutor);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['remediate-candidates'] });
      qc.invalidateQueries({ queryKey: ['scrapers'] });
      setToastMessage({
        type: 'ok',
        text: `Successfully hot-swapped ${res.scraperId} executor to ${res.newExecutor}. Crons will use this on next tick!`,
      });
    },
    onError: (err: any) => {
      setToastMessage({ type: 'error', text: err.message || 'Hot-swap failed' });
    },
  });

  // Open PR mutation
  const openPrM = useMutation({
    mutationFn: () => {
      if (!currentScraper) throw new Error('No scraper selected');
      return openRemediationPR({
        scraperId: currentScraper.id,
        targetExecutor,
        autoMergeOnGreenCi: autoMergeOnCi,
        probeResultsSummary: probeResults
          ? {
              matrix: probeResults.map((r) => ({
                engine: r.engine,
                ok: r.blockVerdict === 'ok' && r.statusCode === 200,
                status: r.statusCode ?? undefined,
                durationMs: r.timing.total,
                blockVerdict: r.blockVerdict,
              })),
              recommendedEngine: targetExecutor === 'docker' ? 'docker_browser' : 'cf_http',
              reason: 'Verified through 1-click Remediator probe comparison.',
            }
          : undefined,
      });
    },
    onSuccess: (res) => {
      setActivePr({ number: res.prNumber, url: res.prUrl, branch: res.branch });
      setToastMessage({
        type: 'ok',
        text: `Created PR #${res.prNumber} (${res.branch}). Auto-merge: ${autoMergeOnCi ? 'ENABLED' : 'DISABLED'}`,
      });
    },
    onError: (err: any) => {
      setToastMessage({ type: 'error', text: err.message || 'Failed to open PR' });
    },
  });

  // Auto-merge status polling when PR is active
  const autoMergeQ = useQuery({
    queryKey: ['auto-merge-status', activePr?.number, targetExecutor],
    queryFn: () => getAutoMergeStatus(activePr!.number, targetExecutor),
    enabled: Boolean(activePr && autoMergeOnCi),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.merged || data?.state === 'closed') return false;
      return 5_000;
    },
  });

  const bestEngine = useMemo(() => {
    if (!probeResults) return null;
    return (
      probeResults.find((r) => r.blockVerdict === 'ok' && r.statusCode === 200) ??
      probeResults.find((r) => r.blockVerdict === 'soft_block') ??
      probeResults[0]
    );
  }, [probeResults]);

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toastMessage && (
        <div
          className={cn(
            'p-3 rounded text-sm flex items-center justify-between border',
            toastMessage.type === 'ok'
              ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
              : 'bg-red-500/10 border-red-500/40 text-red-200',
          )}
        >
          <span>{toastMessage.text}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="text-xs opacity-75 hover:opacity-100 ml-4 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Main Grid: Left = Picker & Probe Config, Right = Results & Remediation Action */}
      <div className="grid grid-cols-1 xl:grid-cols-[440px_1fr] gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          <section className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-400" />
                Select Scraper to Remediate
              </h2>
              <label className="text-xs flex items-center gap-1.5 cursor-pointer text-slate-400">
                <input
                  type="checkbox"
                  checked={blockedOnly}
                  onChange={(e) => setBlockedOnly(e.target.checked)}
                  className="accent-amber-400"
                />
                Blocked only ({candidates.filter((c) => c.lastRunStatus === 'blocked' || c.botRate24h > 0.4).length})
              </label>
            </div>

            {candidatesQ.isLoading && <div className="text-xs text-slate-500">Loading scrapers…</div>}

            <select
              value={currentScraper?.id ?? ''}
              onChange={(e) => setSelectedId(e.target.value)}
              className="input w-full font-medium"
            >
              {sortedCandidates.length === 0 ? (
                <option value="" disabled>
                  {candidatesQ.isLoading ? 'Loading scrapers…' : 'No scrapers available'}
                </option>
              ) : (
                sortedCandidates.map((c) => {
                  const isBlocked = c.lastRunStatus === 'blocked' || c.botRate24h > 0.4;
                  return (
                    <option key={c.id} value={c.id}>
                      {isBlocked ? '🔴 [BLOCKED] ' : c.lastRunStatus === 'ok' ? '🟢 ' : '⚪ '}
                      {c.displayName} ({c.id}) — [{c.executor}]
                    </option>
                  );
                })
              )}
            </select>

            {currentScraper && (
              <div className="border border-slate-800/80 rounded p-3 bg-slate-950/40 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Current Executor:</span>
                  <code className="text-amber-300 font-mono font-semibold">{currentScraper.executor}</code>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Status:</span>
                  <span
                    className={cn(
                      'px-1.5 py-0.5 rounded text-[11px] font-medium border',
                      currentScraper.lastRunStatus === 'blocked'
                        ? 'bg-red-500/20 text-red-200 border-red-500/40'
                        : currentScraper.lastRunStatus === 'ok'
                          ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40'
                          : 'bg-slate-800 text-slate-300 border-slate-700',
                    )}
                  >
                    {currentScraper.lastRunStatus.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Bot Rate (24h):</span>
                  <span className={cn('font-mono font-medium', currentScraper.botRate24h > 0.4 ? 'text-red-400' : 'text-slate-200')}>
                    {pct(currentScraper.botRate24h, 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Last Run:</span>
                  <span className="text-slate-300">{relativeTime(currentScraper.lastRunAt ?? undefined)}</span>
                </div>
                {currentScraper.lastAlert && (
                  <div className="mt-2 pt-2 border-t border-slate-800/60 text-slate-400">
                    <span className="text-amber-300/90 font-medium">Last Alert: </span>
                    {currentScraper.lastAlert.message}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Diagnostic Test Input */}
          <section className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-emerald-400" />
              1-Click Multi-Executor Test
            </h3>
            <p className="text-xs text-slate-400">
              Probes this scraper across edge HTTP, proxy HTTP, and real Chrome CDP browsers to identify which executor bypasses bot protection.
            </p>

            <div className="space-y-1">
              <label className="text-xs text-slate-300 font-medium">Search Keyword to Test</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="iphone 15, ps5"
                className="input w-full font-mono text-xs"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-slate-400">Resolved Target URL</label>
              <div className="text-[11px] font-mono text-slate-300 p-2 bg-slate-950/60 border border-slate-800 rounded break-all">
                {testUrl || '—'}
              </div>
            </div>

            <button
              type="button"
              onClick={() => probeM.mutate()}
              disabled={probeM.isPending || !testUrl}
              className={cn(
                'w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded',
                'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30',
                'disabled:opacity-50 disabled:cursor-not-allowed transition',
              )}
            >
              {probeM.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Probing Candidate Executors…
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" /> Run 4-Engine Diagnostic Probe
                </>
              )}
            </button>
          </section>
        </div>

        {/* Right Column: Diagnostic Matrix & Remediation Actions */}
        <div className="space-y-4 min-w-0">
          {/* Probe Error */}
          {probeM.error && (
            <div className="border border-red-500/30 bg-red-500/10 text-red-200 text-sm rounded p-3">
              {(probeM.error as Error).message}
            </div>
          )}

          {/* Diagnostic Results Matrix */}
          {probeResults && probeResults.length > 0 ? (
            <section className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  Diagnostic Probe Comparison
                </h3>
                {bestEngine && (
                  <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 font-medium">
                    Recommended: <code className="font-bold">{bestEngine.engine}</code>
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {probeResults.map((r) => {
                  const isSuccess = r.blockVerdict === 'ok' && r.statusCode === 200;
                  return (
                    <div
                      key={r.engine}
                      className={cn(
                        'border rounded-lg p-3 space-y-2 transition',
                        isSuccess
                          ? 'border-emerald-500/40 bg-emerald-500/10'
                          : r.blockVerdict === 'soft_block'
                            ? 'border-amber-500/40 bg-amber-500/10'
                            : 'border-red-500/30 bg-red-500/5',
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <code className="text-xs font-bold text-white">{r.engine}</code>
                        <span
                          className={cn(
                            'text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold border',
                            isSuccess
                              ? 'bg-emerald-500/30 text-emerald-200 border-emerald-500/50'
                              : r.blockVerdict === 'soft_block'
                                ? 'bg-amber-500/30 text-amber-200 border-amber-500/50'
                                : 'bg-red-500/30 text-red-200 border-red-500/50',
                          )}
                        >
                          {r.blockVerdict === 'ok' && r.statusCode === 200 ? 'PASSED' : r.blockVerdict.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-xs pt-1">
                        <div>
                          <span className="text-[10px] text-slate-500 block">HTTP STATUS</span>
                          <span className="font-mono text-slate-200">{r.statusCode ?? 'ERR'}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-500 block">LATENCY</span>
                          <span className="font-mono text-slate-200">{r.timing.total}ms</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-500 block">HTML SIZE</span>
                          <span className="font-mono text-slate-200">{(r.bodySizeBytes / 1024).toFixed(1)} KB</span>
                        </div>
                      </div>

                      {r.blockSignals.length > 0 && (
                        <div className="text-[11px] text-red-300/90 pt-1 border-t border-slate-800">
                          Blocked by: {r.blockSignals.map((s) => s.id).join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ) : (
            <div className="border border-dashed border-slate-800 rounded-lg p-8 text-center text-sm text-slate-500">
              Run the 4-Engine Diagnostic Probe on the left to see live comparison across candidate executors.
            </div>
          )}

          {/* Remediation Action Panel */}
          {currentScraper && (
            <section className="bg-slate-900/40 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-400" />
                  Execute Migration
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Choose your target executor and execute an instant zero-downtime hot-swap, or raise a GitHub PR with CI-gated auto-merge.
                </p>
              </div>

              {/* Target Executor Selector */}
              <div className="space-y-1.5">
                <label className="text-xs text-slate-300 font-medium">Target Executor</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['docker', 'cf_workers', 'real_browsers'] as Executor[]).map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      onClick={() => setTargetExecutor(ex)}
                      className={cn(
                        'px-3 py-2 text-xs font-mono font-medium rounded border transition',
                        targetExecutor === ex
                          ? 'border-emerald-500/60 bg-emerald-500/20 text-white shadow-sm'
                          : 'border-slate-800 bg-slate-900/60 text-slate-400 hover:text-white',
                      )}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                {/* Action 1: Instant In-Place Hot-Swap */}
                <div className="border border-slate-800 bg-slate-950/40 rounded-lg p-4 space-y-3 flex flex-col justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-emerald-300 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5" />
                      Option A: Instant In-Place Hot Swap
                    </h4>
                    <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                      Updates MongoDB registry immediately. The cron scheduler picks it up on the next tick with zero deployment delay.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => hotSwapM.mutate()}
                    disabled={hotSwapM.isPending}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 transition disabled:opacity-50"
                  >
                    {hotSwapM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Hot Swap to {targetExecutor} Now
                  </button>
                </div>

                {/* Action 2: Raise PR + Auto-Merge */}
                <div className="border border-slate-800 bg-slate-950/40 rounded-lg p-4 space-y-3 flex flex-col justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-sky-300 flex items-center gap-1.5">
                      <GitPullRequest className="w-3.5 h-3.5" />
                      Option B: Raise PR &amp; Auto-Merge
                    </h4>
                    <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                      Creates a git branch and opens a Pull Request on GitHub. Automatically merges when CI checks are green!
                    </p>
                    <label className="mt-2 flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoMergeOnCi}
                        onChange={(e) => setAutoMergeOnCi(e.target.checked)}
                        className="accent-sky-400"
                      />
                      Auto-merge PR when CI is green
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => openPrM.mutate()}
                    disabled={openPrM.isPending}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded bg-sky-500/20 border border-sky-500/40 text-sky-200 hover:bg-sky-500/30 transition disabled:opacity-50"
                  >
                    {openPrM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitPullRequest className="w-3.5 h-3.5" />}
                    Raise PR on GitHub
                  </button>
                </div>
              </div>

              {/* Active PR CI Tracking Banner */}
              {activePr && (
                <div className="border border-sky-500/30 bg-sky-500/10 rounded-lg p-3 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-sky-200 font-semibold flex items-center gap-1.5">
                      <GitPullRequest className="w-4 h-4" />
                      Active Remediation PR #{activePr.number}
                    </span>
                    <a
                      href={activePr.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-300 hover:text-white inline-flex items-center gap-1 text-[11px] underline"
                    >
                      View on GitHub <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-slate-400">CI Status:</span>
                    <span
                      className={cn(
                        'px-1.5 py-0.5 rounded text-[10px] font-mono uppercase font-semibold border',
                        autoMergeQ.data?.ciStatus === 'pass'
                          ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40'
                          : autoMergeQ.data?.ciStatus === 'fail'
                            ? 'bg-red-500/20 text-red-200 border-red-500/40'
                            : 'bg-amber-500/20 text-amber-200 border-amber-500/40',
                      )}
                    >
                      {autoMergeQ.data?.ciStatus ?? 'PENDING'}
                    </span>
                    {autoMergeQ.data?.merged && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/30 text-emerald-200 border border-emerald-500/50">
                        🎉 SQUASH MERGED
                      </span>
                    )}
                    <span className="text-[11px] text-slate-400">{autoMergeQ.data?.message}</span>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
